import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { CreateProjectService } from '../../services/create-project.service';
import { UploadConfirmationDialogComponent } from '../../components/bulk-upload-confirmation-dialog/bulk-upload-confirmation-dialog.component';
import { DocumentTypeIconComponent } from '../document-type-icon/document-type-icon.component';

// Lets the user copy documents from a "source" project (the one this project was
// duplicated from) into the current project. Preview/download of an individual
// source document are delegated back to the parent, which owns that logic.
@Component({
  selector: 'app-copy-source-documents',
  standalone: true,
  imports: [CommonModule, MatIconModule, DocumentTypeIconComponent],
  templateUrl: './copy-source-documents.component.html',
  styleUrl: './copy-source-documents.component.css'
})
export class CopySourceDocumentsComponent implements OnInit, OnChanges {
  @Input() projectId: any;
  @Input() projectName: any;
  @Input() workDayId: any;
  @Input() userName: any;
  @Input() selectedDepartmentIds: string[] = [];
  @Input() allAvailableDepartments: any[] = [];
  // Used to detect when an uploaded copy is removed elsewhere so its selection can be cleared.
  @Input() userUploads: any[] = [];

  @Output() documentsUploaded = new EventEmitter<void>();
  @Output() previewRequested = new EventEmitter<any>();
  @Output() downloadRequested = new EventEmitter<any>();

  sourceProjectId: string | null = null;
  sourceProjectDocuments: any[] = [];

  constructor(
    private readonly createProjectService: CreateProjectService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog
  ) {}

  get isCopyMode(): boolean {
    return !!this.sourceProjectId;
  }

  ngOnInit(): void {
    this.sourceProjectId = this.createProjectService.getSourceProjectId();
    if (this.isCopyMode) {
      this.fetchSourceProjectDocuments();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userUploads'] && !changes['userUploads'].firstChange) {
      this.syncSourceSelectionWithUploads();
    }
  }

  private fetchSourceProjectDocuments(): void {
    if (!this.sourceProjectId) {
      return;
    }
    const selectedIds = new Set(this.createProjectService.getSelectedSourceDocumentIds() || []);
    this.createProjectService.documentFetchByProjectId(this.sourceProjectId, this.workDayId).subscribe({
      next: (docs: any[]) => {
        const active = (docs || []).filter((d: any) => !d.is_deleted);
        this.sourceProjectDocuments = this.dedupeSourceDocuments(active)
          .map((d: any) => ({ ...d, checked: selectedIds.has(String(d.file_versioning_id)) }));
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.trace('Failed to fetch source project documents', err);
      }
    });
  }

  // documentFetchByProjectId returns one row per department/version, so collapse to
  // one row per file (latest version) and aggregate the departments it belongs to.
  private dedupeSourceDocuments(docs: any[]): any[] {
    const groups = new Map<string, any[]>();
    for (const d of docs) {
      const key = String(d.file_name ?? d.file_versioning_id ?? '').toLowerCase();
      const group = groups.get(key) ?? [];
      group.push(d);
      groups.set(key, group);
    }
    return Array.from(groups.values()).map(items => {
      const rep = items.reduce((a, b) => (Number(b.version_number || 0) > Number(a.version_number || 0) ? b : a), items[0]);
      const departmentIds = Array.from(new Set(
        items.map(i => String(i.department_id ?? '')).filter(id => !!id)
      ));
      return { ...rep, department_ids: departmentIds };
    });
  }

  isAllSourceSelected(): boolean {
    return this.sourceProjectDocuments.length > 0 && this.sourceProjectDocuments.every(d => d.checked);
  }

  toggleSourceAll(): void {
    const val = !this.isAllSourceSelected();

    // Selecting all: warn on duplicates, then open the per-file department picker
    if (val) {
      const uploadedFileNames = this.getUploadedFileNames();
      const duplicates = this.sourceProjectDocuments
        .map(d => d.file_name)
        .filter(name => uploadedFileNames.has((name || '').toLowerCase()));

      if (duplicates.length > 0) {
        this.showDuplicateWarning(duplicates);
        return;
      }

      const dialogRef = this.dialog.open(UploadConfirmationDialogComponent, {
        width: '560px',
        data: {
          isBulk: true,
          perFileDepartments: true,
          availableDepartments: this.getDepartmentOptions(),
          files: this.sourceProjectDocuments.map(d => ({
            key: String(d.file_versioning_id),
            name: d.file_name,
            departmentIds: this.getDefaultFileDepartments(d)
          }))
        }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result?.confirmed) {
          const uploadedKeys = new Set(result.selections.map((s: any) => s.key));
          this.sourceProjectDocuments.forEach(d => {
            if (uploadedKeys.has(String(d.file_versioning_id))) {
              d.checked = true;
            }
          });
          this.uploadSourceSelections(result.selections);
          this.persistSelectedSourceDocuments();
        }
      });

      return;
    }

    // Deselecting all: just uncheck without confirmation
    this.sourceProjectDocuments.forEach(d => d.checked = false);
    this.persistSelectedSourceDocuments();
  }

  toggleSourceFileSelection(file: any): void {
    file.checked = !file.checked;

    // Open the per-file department picker before copying a single source document
    if (file.checked) {
      // Already copied previously (and not removed from Your Upload): don't re-upload a
      // duplicate, just warn and leave it unchecked since there's nothing new to copy.
      if (this.getUploadedFileNames().has((file.file_name || '').toLowerCase())) {
        this.showDuplicateWarning([file.file_name]);
        file.checked = false;
        this.persistSelectedSourceDocuments();
        return;
      }

      const dialogRef = this.dialog.open(UploadConfirmationDialogComponent, {
        width: '560px',
        data: {
          isBulk: false,
          perFileDepartments: true,
          availableDepartments: this.getDepartmentOptions(),
          files: [{
            key: String(file.file_versioning_id),
            name: file.file_name,
            departmentIds: this.getDefaultFileDepartments(file)
          }]
        }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result?.confirmed && result.selections?.length) {
          this.uploadSourceSelections(result.selections);
        } else {
          file.checked = false;
        }
        this.persistSelectedSourceDocuments();
      });

      return;
    }

    this.persistSelectedSourceDocuments();
  }

  private getDepartmentOptions(): Array<{ id: string; name: string }> {
    if (this.allAvailableDepartments?.length) {
      return this.allAvailableDepartments.map((d: any) => ({ id: String(d.id), name: d.name || '' }));
    }
    return (this.selectedDepartmentIds || []).map((id: any) => ({ id: String(id), name: '' }));
  }

  private getUploadedFileNames(): Set<string> {
    return new Set(
      this.userUploads.map((u: any) => String(u.file_name || u.name || '').toLowerCase())
    );
  }

  private showDuplicateWarning(duplicates: string[]): void {
    this.dialog.open(UploadConfirmationDialogComponent, {
      width: '500px',
      data: {
        warningOnly: true,
        title: 'Duplicate Files Detected',
        message: `The following file(s) have already been uploaded. Please remove the existing file(s) and upload again:\n\n${duplicates.join('\n')}`,
        fileNames: duplicates
      }
    });
  }

  // Preselect the document's own department(s) that are available, else the chosen upload departments
  private getDefaultFileDepartments(doc: any): string[] {
    const optionIds = new Set(this.getDepartmentOptions().map(o => o.id));
    const own: string[] = (doc?.department_ids?.length ? doc.department_ids : [doc?.department_id])
      .map((id: any) => String(id ?? ''))
      .filter((id: string) => !!id);
    const matched = own.filter((id: string) => optionIds.has(id));
    return matched.length ? matched : [...this.selectedDepartmentIds];
  }

  private uploadSourceSelections(selections: Array<{ key: string; departmentIds: string[] }>): void {
    const items = selections
      .map(sel => {
        const doc = this.sourceProjectDocuments.find(d => String(d.file_versioning_id) === sel.key);
        return doc ? { doc, departmentIds: sel.departmentIds } : null;
      })
      .filter((x): x is { doc: any; departmentIds: string[] } => !!x && x.departmentIds.length > 0);
    this.uploadSourceItemsSequentially(items, 0);
  }

  private uploadSourceItemsSequentially(items: Array<{ doc: any; departmentIds: string[] }>, index: number): void {
    if (index >= items.length) {
      this.documentsUploaded.emit();
      return;
    }
    const { doc, departmentIds } = items[index];
    this.createProjectService.uploadDocument(this.workDayId, this.buildSourceUploadPayload(doc, departmentIds)).subscribe({
      next: () => this.uploadSourceItemsSequentially(items, index + 1),
      error: (error) => {
        console.trace('Error copying source document', error);
        this.uploadSourceItemsSequentially(items, index + 1);
      }
    });
  }

  private buildSourceUploadPayload(file: any, departmentIds: string[]): any {
    const payload = {
      projectId: this.projectId,
      projectName: this.projectName,
      departmentIds: departmentIds,
      fileName: file.file_name,
      userName: this.userName,
      uploadedBy: this.userName,
      isCollectionDoc: true,
      collectionFilePath: file.file_path,
      contentType: DocumentTypeIconComponent.getMimeType(file.file_name)
    };
    console.info('Copy source doc payload', payload);
    return payload;
  }

  private persistSelectedSourceDocuments(): void {
    const ids = this.sourceProjectDocuments
      .filter(d => d.checked)
      .map(d => String(d.file_versioning_id));
    this.createProjectService.setSelectedSourceDocumentIds(ids);
  }

  // Clear a copied source document's selection once its uploaded copy is removed and no matching upload remains.
  private syncSourceSelectionWithUploads(): void {
    if (!this.isCopyMode || this.sourceProjectDocuments.length === 0) {
      return;
    }
    const uploadedNames = new Set(
      this.userUploads
        .map((u: any) => String(u?.file_name || u?.name || '').toLowerCase())
        .filter((name: string) => !!name)
    );
    let changed = false;
    this.sourceProjectDocuments.forEach(doc => {
      if (doc.checked && !uploadedNames.has(String(doc.file_name || '').toLowerCase())) {
        doc.checked = false;
        changed = true;
      }
    });
    if (changed) {
      this.persistSelectedSourceDocuments();
      this.cdr.detectChanges();
    }
  }
}
